import { minBy, round, sum } from 'lodash';

const epsilon = 10e-6;

export const isRoughlyEqual = (a: number, b: number) =>
  Math.abs(a - b) < epsilon;

export const roundPixel = (n: number) => round(n, 3);

export const roundTime = (n: number) => Math.round(n);

/**
 * Find the index of the percentile in an array of values
 * @param array an array of numeric values
 * @param percentile the desired percentile; must be in the range [0-1]
 * @returns the value of `arr` for the specific percentage
 */
export const getPercentile = (array: number[], percentile: number) => {
  if (array.length === 0) {
    throw new Error('The collection should not be empty');
  } else if (percentile <= 0 || percentile >= 1) {
    throw new Error('The percentile should be in the range of [0-1]');
  }

  const sorted = array.slice().sort((a, b) => a - b);
  const value = (sorted.length - 1) * percentile;
  const indexArray = Array.from(Array(array.length).keys());
  const closestIndex = minBy(indexArray, (index) => Math.abs(value - index));

  // the closestIndex's type will always be a number
  // since we already checked above if the collection is empty
  return sorted[closestIndex as number];
};

export const squaredDistance = (
  compareVector: (number | null)[],
  pinnedVector: number[],
) => {
  return sum(
    compareVector.map((x, i) => {
      if (x == null) {
        return pinnedVector[i] ** 2;
      }
      return (x - pinnedVector[i]) ** 2;
    }),
  );
};

export const median = (array: number[]) => {
  if (array.length === 0) {
    return null;
  }
  const sortedArray = [...array].sort((a, b) => b - a);
  const midIndex = Math.floor(sortedArray.length / 2);
  return sortedArray.length % 2 === 0
    ? (sortedArray[midIndex - 1] + sortedArray[midIndex]) / 2
    : sortedArray[midIndex];
};

/**
 * Find the index of the maximum value in an array
 * equilvalent to numpy.argmax
 * @param arr an array of numeric values
 * @returns indice of the maximum value
 */
export const argMax = (arr: number[]) =>
  arr.map((x, i) => [x, i]).reduce((r, a) => (a[0] > r[0] ? a : r))[1];

/**
 * Find the index of the minimum value in an array
 * equilvalent to numpy.argmin
 * @param arr an array of numeric values
 * @returns indice of the minimum value
 */
export const argMin = (arr: number[]) =>
  arr.map((x, i) => [x, i]).reduce((r, a) => (a[0] < r[0] ? a : r))[1];

/**
 * Return evenly spaced numbers over a specified interval.
 * equilvalent to numpy.linspace
 * @param start The starting value of the sequence.
 * @param stop The end value of the sequence.
 * @param size Number of samples to generate.
 * @returns Array of evenly spaced values in the closed interval [start, stop]
 */
export const getLinSpace = (start: number, stop: number, size: number) => {
  const arr = [];
  const step = (stop - start) / (size - 1);
  for (let i = 0; i < size; i++) {
    arr.push(start + step * i);
  }
  return arr;
};
